import React, { useCallback, useMemo } from 'react'
import { AxeActor, AxeEvent, AxeSubscriptionIdentifier, EntityType, EntityTypeEnum, SubscriptionFilter } from 'api'
import { AxeActions } from 'api/AxeAction'
import { AxeDetail } from 'selectors/axeSelectors'
import { GridId } from 'store/grid'
import { RepositoryActions } from 'store/repository'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { sliceFieldName } from 'utils'
import { gridOptionsBase, DataGrid } from 'view/components/datagrid'
import { getAxeGridColumnDefs } from './components/AxeGridModel'
import { AxeGridToolbarSwitches } from './components/AxeGridToolbarSwitches'
import { AxeFieldName, AxeFieldNames } from './components/AxeGridFields'
import { canPerformEvent } from './components/axeModelUtils'
import { createCollateralsFromInstrumentIdGetterSelector } from 'selectors/instrumentSelectors'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import { createEntitySnapshotLoadedSelector } from 'selectors/systemSelectors'
import { GridOptions } from 'ag-grid-community'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { AxeBoardFilters } from 'view/routes/axeBoard/components/AxeBoardFilters'

interface AxeGridProps {
  viewAs: AxeActor
  gridId: GridId.AxeGridInitiator | GridId.AxeGridResponder
  isAdmin: boolean
  axeDetails: AxeDetail[]
  subscriptionDetail?: SubscriptionDetail
  snapshotLoaded: boolean
  handlePageSubscribe: (filters: SubscriptionFilter[], pageNumber: number) => ReturnType<typeof AxeActions.pageSubscribe>
  getCollateralsFromInstrumentId: ReturnType<ReturnType<typeof createCollateralsFromInstrumentIdGetterSelector>>
  onUpdateField: typeof RepositoryActions.updateField
}

const AxeGridOptions: GridOptions<AxeDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.axe.axeId
}

const AxeGridComponent: React.FC<AxeGridProps> = ({
  axeDetails,
  gridId,
  isAdmin,
  snapshotLoaded,
  subscriptionDetail,
  viewAs,
  getCollateralsFromInstrumentId,
  handlePageSubscribe,
  onUpdateField
}) => {
  const axeGridColumnDefs = useMemo(() => getAxeGridColumnDefs(viewAs, isAdmin), [viewAs, isAdmin])
  const onPageChanged = useCallback(
    (pageNumber: number) => {
      if (subscriptionDetail) {
        handlePageSubscribe(subscriptionDetail.filters, pageNumber)
      }
    },
    [handlePageSubscribe, subscriptionDetail]
  )

  const handleCellValueUpdated = useCallback(
    (_rowId: string, { axe }: AxeDetail, fieldName: AxeFieldName, value: string) => {
      switch (fieldName) {
        case AxeFieldNames.INSTRUMENT:
          const { collateral, collateralBasket } = getCollateralsFromInstrumentId(value)
          onUpdateField(EntityType.AXE, axe.axeId, sliceFieldName(AxeFieldNames.COLLATERAL, 1), collateral?.id ?? null)
          onUpdateField(EntityType.AXE, axe.axeId, sliceFieldName(AxeFieldNames.COLLATERAL_BASKET, 1), collateralBasket?.id ?? null)
          break
        default:
          onUpdateField(EntityType.AXE, axe.axeId, sliceFieldName(fieldName, 1), value)
      }
    },
    [getCollateralsFromInstrumentId, onUpdateField]
  )

  const shouldRedrawOnCellValueUpdate = useCallback((field: AxeFieldName) => {
    const fields: AxeFieldName[] = [AxeFieldNames.REPO_TYPE, AxeFieldNames.INSTRUMENT]
    return fields.includes(field)
  }, [])

  return (
    <DataGrid<AxeDetail>
      gridId={gridId}
      gridClass="upload-grid"
      gridOptions={AxeGridOptions}
      disableGrouping={true}
      disableExport={true}
      columnDefs={axeGridColumnDefs}
      shouldRedrawOnCellValueUpdate={shouldRedrawOnCellValueUpdate}
      rowData={axeDetails}
      canEditRow={axeDetail => canPerformEvent(axeDetail, AxeEvent.UPDATE_ATTRIBUTES)}
      customToolbarSwitches={AxeGridToolbarSwitches}
      subscriptionDetail={subscriptionDetail}
      onPageChanged={onPageChanged}
      onCellValueUpdated={handleCellValueUpdated}
      showLoader={!snapshotLoaded}
      subscriptionFilters={() => (
        <AxeBoardFilters
          subscriptionIdentifier={
            viewAs === AxeActor.INITIATOR ? AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR : AxeSubscriptionIdentifier.AXE_TODAY_RESPONDER
          }
        />
      )}
    />
  )
}

export const AxeGridContainer = connect(
  createStructuredSelector({
    isAdmin: isAdminGatewayUserSelector,
    snapshotLoaded: createEntitySnapshotLoadedSelector(EntityTypeEnum.AXE),
    getCollateralsFromInstrumentId: createCollateralsFromInstrumentIdGetterSelector()
  }),
  {
    onUpdateField: RepositoryActions.updateField
  }
)(AxeGridComponent)
